/* DemoBanner — surfaces only when the current user owns a demo client.

   Shown at the top of every authenticated screen (slotted by AppLayout
   right above the <Outlet />). Three states:
     - loading      → renders nothing (avoid a flash on every page nav)
     - has demo     → renders the dismissible banner
     - no demo      → renders nothing

   Dismissal is per-browser via localStorage. The data still lives on
   the server: the user can re-show the banner from Settings → Demo
   if we ever add that. Until then, "Elimina dati demo" both removes
   the demo and dismisses the banner (no need to clear LS).

   The "Crea il tuo primo cliente" CTA points to /app/clients?new=1 so
   the existing Clients page can pop the "new client" sheet. If the
   query param isn't wired yet, the user just lands on the list — still
   a useful action.
*/
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, UserPlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const LS_DISMISS_KEY = "abm.demoBanner.dismissed";

export default function DemoBanner() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, has_demo: false, client_id: null });
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(LS_DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);

  // Fetch once on mount. The banner is short-lived (one demo per user)
  // so we don't bother polling; the dashboard already refetches on nav.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/demo/status");
        if (alive) setState({ loading: false, has_demo: !!data?.has_demo, client_id: data?.client_id || null });
      } catch {
        if (alive) setState({ loading: false, has_demo: false, client_id: null });
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleDismiss = useCallback(() => {
    try { localStorage.setItem(LS_DISMISS_KEY, "1"); } catch { /* private mode */ }
    setDismissed(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (busy) return;
    if (!window.confirm("Eliminare definitivamente tutti i dati di esempio?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/demo/reset");
      const c = data?.deleted_clients || 0;
      const e = data?.deleted_events || 0;
      toast.success(`Dati demo eliminati (${c} cliente, ${e} evento).`);
      setState({ loading: false, has_demo: false, client_id: null });
      // Also clear the dismiss flag so the banner doesn't reappear on
      // a future demo (e.g. if we ever expose a "Reseed demo" button).
      try { localStorage.removeItem(LS_DISMISS_KEY); } catch { /* */ }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Errore durante l'eliminazione");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  if (state.loading || !state.has_demo || dismissed) return null;

  return (
    <div
      className="relative bg-gradient-to-r from-[#FBF6EC] via-[#F5EBD7] to-[#FBF6EC] border-b border-primary/30"
      data-testid="demo-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
        <span className="inline-flex w-10 h-10 rounded-full bg-primary/15 ring-1 ring-primary/30 items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
        </span>
        <p className="flex-1 text-sm text-neutral-800 leading-relaxed">
          Stai visualizzando un esempio dimostrativo precompilato per esplorare tutte le funzionalità della piattaforma.
        </p>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button
            type="button"
            size="sm"
            variant="default"
            className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
            onClick={() => navigate("/app/clients?new=1")}
            data-testid="demo-banner-create-cta"
          >
            <UserPlus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            Crea il tuo primo cliente
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-sm border-primary/30 text-neutral-800 hover:bg-white"
            onClick={handleDelete}
            disabled={busy}
            data-testid="demo-banner-delete-cta"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            {busy ? "Elimino..." : "Elimina dati demo"}
          </Button>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-2 right-2 sm:static sm:ml-2 p-1.5 rounded-full text-neutral-500 hover:text-neutral-900 hover:bg-white/60 transition-colors"
          aria-label="Chiudi"
          data-testid="demo-banner-dismiss"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
